// Type a number in any format, or search contacts by name/number. Used by New text and reminders.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPhone, normalizeE164 } from '@bt/core';
import { api, type Contact } from '../api';

export interface Picked {
  contact?: Contact;
  phone_number?: string;
}

export function ContactPicker({
  onPick,
  autoFocus,
  placeholder = 'Name or phone number',
}: {
  onPick: (p: Picked) => void;
  autoFocus?: boolean;
  placeholder?: string;
}) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(text.trim()), 200);
    return () => window.clearTimeout(t);
  }, [text]);

  const q = useQuery({
    queryKey: ['contacts', 'pick', debounced],
    queryFn: () => api.contacts.list({ q: debounced, limit: 8 }),
    enabled: debounced.length >= 1,
    staleTime: 10_000,
  });

  const e164 = normalizeE164(text);
  const items = q.data?.items ?? [];
  const showNumber = !!e164 && !items.some((c) => c.phone_number === e164);

  return (
    <div className="picker">
      <input
        className="input"
        value={text}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (items.length > 0 && !showNumber) onPick({ contact: items[0] });
            else if (e164) onPick({ phone_number: e164 });
          }
        }}
      />
      {open && text.trim() && (
        <div className="menu">
          {showNumber && (
            <button type="button" onMouseDown={() => onPick({ phone_number: e164! })}>
              Text {formatPhone(e164!)} <span className="muted small">(new number)</span>
            </button>
          )}
          {items.map((c) => (
            <button type="button" key={c.id} onMouseDown={() => onPick({ contact: c })}>
              {c.name ? (
                <>
                  {c.name} <span className="muted small">{formatPhone(c.phone_number)}</span>
                </>
              ) : (
                formatPhone(c.phone_number)
              )}
              {c.opted_out && <span className="chip danger" style={{ marginLeft: 6 }}>OPTED OUT</span>}
            </button>
          ))}
          {!showNumber && items.length === 0 && !q.isFetching && (
            <div className="muted small" style={{ padding: '8px 10px' }}>
              {text.replace(/\D/g, '').length >= 7 ? 'That does not look like a full US number.' : 'No matches. Type a full phone number to text a new person.'}
            </div>
          )}
          {q.isFetching && (
            <div className="muted small" style={{ padding: '8px 10px' }}>
              Searching
            </div>
          )}
        </div>
      )}
    </div>
  );
}
